import { createRouter, createWebHistory, Router, RouteRecordRaw } from 'vue-router';

import {adminRouter} from "./admin.router";
import {authRouter} from "@app/router/auth.router";
import {customerRouter} from "@app/router/customer.router";
import {platformRouter} from "@app/router/platform.router";
import {AdminRouteNames, CustomerRouteNames} from "@app/router/route-names";
import {App, URLOpenListenerEvent} from "@capacitor/app";
import {Capacitor} from "@capacitor/core";
import { trackEvent } from '@app/composables/posthog';
import { useDrawer } from '@torim-app/shared-components';

const NotFoundView = () => import("../views/NotFound.vue");

const routes: RouteRecordRaw[] = [
  ...authRouter,
  ...adminRouter,
  ...customerRouter,
  ...platformRouter,
  {
    path: '/404',
    name: 'NotFound',
    component: NotFoundView
  },
  {
    path: '/:catchAll(.*)', redirect: '404'
  }
]

export const createRouterObject = () => {
  const router = createRouter({
    history: createWebHistory(),
    routes
  });

  // WhatsApp Business connect (native): Meta's OAuth redirect_uri is registered as
  // /admin/configurations, but the connect flow now lives on the Integrations page. Land
  // the callback there so it can finish the token exchange (templates + completion).
  router.beforeEach((to) => {
    if (to.path === '/admin/configurations' && to.query.state === 'wa_connect' && to.query.code) {
      return { name: AdminRouteNames.Integrations, query: { code: to.query.code, state: to.query.state } };
    }
    return true;
  });

  router.afterEach((to, from) => {
    const slidersDestinations = [CustomerRouteNames.AppointmentView, CustomerRouteNames.Schedule];
    if (slidersDestinations.includes(<CustomerRouteNames>to.name) && <CustomerRouteNames>from.name === CustomerRouteNames.Home)
      to.meta.transition = 'slide-up'
    else if (slidersDestinations.includes(<CustomerRouteNames>from.name) && <CustomerRouteNames>to.name === CustomerRouteNames.Home)
      from.meta.transition = 'slide-down'

    trackEvent('page_viewed', {
      page_name: to.name as string,
      page_path: to.path,
      from_page: from.name as string,
    });

    try{
      useDrawer().closeDrawer();
    }catch (e) {
      console.error(`Failed to close drawer: ${e}`);
    }

  });

  const navigateToUrl = (url: string, router: Router) => {
    if (!import.meta.env.VITE_DASHBOARD_URL) return;
    console.log(`deep link navigate: ${url}`);
    try {
      const parsed = new URL(url);
      const path = parsed.pathname || '/';
      const query: Record<string, string> = {};
      parsed.searchParams.forEach((value, key) => { query[key] = value; });
      if (path && path !== '/') {
        router.push({ path, query });
      }
    } catch {
      // Fallback for non-standard URLs
      const slug = url.replace(import.meta.env.VITE_DASHBOARD_URL, '');
      if (slug) {
        router.push({ path: slug });
      }
    }
  };

  // Handle deep links when app is already running
  App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
    navigateToUrl(event.url, router);
  });

  // Handle deep links on cold start (app opened fresh from a link)
  if (Capacitor.isNativePlatform()) {
    router.isReady().then(() => {
      App.getLaunchUrl().then((result) => {
        if (result?.url) {
          console.log(`cold start deep link: ${result.url}`);
          navigateToUrl(result.url, router);
        }
      });
    });
  }

  return router;
}
